import { GetProp, Upload, UploadProps } from "antd";
import { LoaderCircleIcon, UploadCloudIcon } from "lucide-react";
import { getUploadAuthHeaders } from "@/lib/adminTenantContext";
import { UploadWithCrop } from "../UploadWithCrop";

const ACCEPTED_IMAGE_TYPES = "image/jpeg,.jpg,.jpeg,image/png,.png";

interface Props extends UploadProps {
  uploading: boolean;
  aspect?: number | "any"; // Aspect ratio for cropping
  enableCrop?: boolean; // Whether to enable cropping
  folder?: "hotels" | "activities" | "transports" | "tenant-images" | null;
  actionPath?: string;
  minWidth?: number;
  minHeight?: number;
  requiredAspect?: number;
  cropOnlyWhenAspectMismatch?: boolean;
  cropQuality?: number;
  /** Called when local validation rejects a selected image. */
  onValidationError?: (error: string) => void;
}

/** Uploads JPG or PNG artwork after validating file size and optional dimensions. */
export const ImageUpload = (props: Props = { uploading: false, enableCrop: true, folder: null }) => {
  type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];
  // rc-upload treats `folder` as directory-selection mode, so internal props must not be forwarded.
  const {
    actionPath,
    aspect,
    cropOnlyWhenAspectMismatch,
    cropQuality,
    enableCrop,
    folder,
    minHeight,
    minWidth,
    onValidationError,
    requiredAspect,
    uploading,
    ...uploadProps
  } = props;

  const beforeUpload = (file: FileType) => {
    const reportValidationError = (error: string) => {
      onValidationError?.(error);
    };
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      reportValidationError("Upload a JPG or PNG image.");
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      reportValidationError("Image must be smaller than 2 MB.");
    }

    if (!isJpgOrPng || !isLt2M) {
      return Upload.LIST_IGNORE;
    }

    if (!minWidth && !minHeight && !requiredAspect) {
      return true;
    }

    return new Promise<boolean | typeof Upload.LIST_IGNORE>((resolve) => {
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(image.src);
        const requiredWidth = minWidth ?? 0;
        const requiredHeight = minHeight ?? 0;
        if (image.width < requiredWidth || image.height < requiredHeight) {
          reportValidationError(`Image must be at least ${requiredWidth}x${requiredHeight} pixels.`);
          resolve(Upload.LIST_IGNORE);
          return;
        }
        if (requiredAspect && Math.abs(image.width - image.height * requiredAspect) > 1) {
          reportValidationError(`Image must use a ${requiredAspect}:1 aspect ratio.`);
          resolve(Upload.LIST_IGNORE);
          return;
        }
        resolve(true);
      };
      image.onerror = () => {
        URL.revokeObjectURL(image.src);
        reportValidationError("We could not validate the image dimensions.");
        resolve(Upload.LIST_IGNORE);
      };
      image.src = URL.createObjectURL(file as Blob);
    });
  };

  const action = actionPath || `/api/generics/upload${folder ? `/${folder}` : ""}`;

  if (enableCrop) {
    return (
      <UploadWithCrop
        {...uploadProps}
        aspect={aspect}
        cropOnlyWhenAspectMismatch={cropOnlyWhenAspectMismatch}
        cropQuality={cropQuality}
        action={action}
        listType="picture-card"
        headers={getUploadAuthHeaders()}
        name="image"
        accept={ACCEPTED_IMAGE_TYPES}
        className={`avatar-uploader ${uploadProps.className || ""}`}
        showUploadList={true}
        beforeUpload={beforeUpload}
        maxCount={1}
      >
        {uploadProps.fileList?.length == 0 && (
          <button
            type="button"
            className="flex h-full w-full flex-col items-center justify-center gap-1.5 border-0 bg-transparent text-xs font-medium text-gray-500 transition-colors hover:text-[#131B2A]"
          >
            {uploading ? (
              <>
                <LoaderCircleIcon className="animate-spin" size={16} />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <UploadCloudIcon size={17} />
                <span>Upload image</span>
              </>
            )}
          </button>
        )}
      </UploadWithCrop>
    );
  } else {
    return (
      <Upload
        {...uploadProps}
        action={action}
        listType="picture-card"
        headers={getUploadAuthHeaders()}
        name="image"
        accept={ACCEPTED_IMAGE_TYPES}
        className={`avatar-uploader ${uploadProps.className || ""}`}
        showUploadList={true}
        beforeUpload={beforeUpload}
        maxCount={1}
      >
        {uploadProps.fileList?.length == 0 && (
          <button
            type="button"
            className="flex h-full w-full flex-col items-center justify-center gap-1.5 border-0 bg-transparent text-xs font-medium text-gray-500 transition-colors hover:text-[#131B2A]"
          >
            {uploading ? (
              <>
                <LoaderCircleIcon className="animate-spin" size={16} />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <UploadCloudIcon size={17} />
                <span>Upload image</span>
              </>
            )}
          </button>
        )}
      </Upload>
    );
  }
};
